import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  ChevronUp,
  Clock,
  MapPin,
  Pencil,
  Phone,
  Repeat,
  Send,
  Users,
  Video,
  X,
} from "lucide-react";
import { Button, EmptyState, StatusBadge } from "@/design-system/components/ui";
import type { StatusTone } from "@/design-system/components/ui";
import { ResourceDrawer } from "@/components/portfolioAdmin/loanDetail/common/ResourceDrawer";
import { ConfirmModal } from "@/components/ui/PromptModals";
import { useToast } from "@/components/ui/Toast";
import { useUserTimeZone } from "@/utils/datetime";
import { cn } from "@/design-system/lib/utils";
import { safeHref } from "@/design-system/lib/safeHref";
import { formatDateInTz, formatTimeInTz } from "@/design-system/lib/format";
import { zoneCaption } from "@/lib/appointmentZones";
import { extractErrorMessage } from "@/api/client";
import type {
  AppointmentAttendeeResponse,
  AppointmentDto,
  RecurrenceEditScope,
} from "@/api/appointments";
import {
  useCancelAppointment,
  useResendInvitations,
  useRespond,
} from "../hooks/useAppointments";
import { RecurrenceScopeDialog } from "./RecurrenceScopeDialog";

interface Props {
  open: boolean;
  onClose: () => void;
  /** The selected day, `YYYY-MM-DD` in the user's timezone. */
  dayIso: string;
  appointments: AppointmentDto[];
  onEdit: (appointment: AppointmentDto) => void;
}

const RESPONSE_TONE: Record<AppointmentAttendeeResponse, StatusTone> = {
  Invited: "neutral",
  Accepted: "ok",
  Declined: "danger",
};

export function AppointmentDayDrawer({
  open,
  onClose,
  dayIso,
  appointments,
  onEdit,
}: Props) {
  const tz = useUserTimeZone();
  // `dayIso` is a wall-calendar date; render it tz-agnostically as noon-UTC.
  const heading = dayIso
    ? formatDateInTz(`${dayIso}T12:00:00.000Z`, tz, {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : "";

  // One caption per surface rather than a suffix on every row. This drawer shows
  // a SINGLE day, so its offset is true of every row beneath it and the full
  // name + offset form is safe here.
  const caption = zoneCaption(tz, dayIso ? new Date(`${dayIso}T12:00:00.000Z`) : null);
  const count =
    appointments.length === 1 ? "1 appointment" : `${appointments.length} appointments`;

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      title={heading || "Day"}
      subtitle={caption ? `${count} · ${caption}` : count}
      width="md"
    >
      {appointments.length === 0 ? (
        <EmptyState
          icon={Clock}
          title="Nothing scheduled"
          description="No appointments on this day."
        />
      ) : (
        <ul className="flex flex-col gap-space-3 m-0 p-0 list-none">
          {appointments.map((appt) => (
            <AppointmentRow key={appt.id} appointment={appt} tz={tz} onEdit={onEdit} />
          ))}
        </ul>
      )}
    </ResourceDrawer>
  );
}

/**
 * A long free-text block, collapsed to `lines` with a Show more / Show less
 * toggle. Used for the appointment description (four) and the dial-in details
 * (six — see CLAMP_CLASS). Mirrors the summary toggle on `BrokerDetailPage` (text
 * Button + chevron + `aria-expanded`).
 *
 * `CalendarAppointment.Description` is `nvarchar(max)` and the Calendar AI
 * Create+ path fills it from arbitrary pasted text, so it can be long enough
 * to bury the rest of the card. Display-only: the stored text is never
 * altered, and the full value stays in the DOM (just clipped) so find-in-page
 * and copy still reach it.
 *
 * Overflow is MEASURED, not inferred from the string's length — it depends on
 * the rendered width, font metrics and embedded newlines, and this drawer is
 * responsive. A character-count heuristic is wrong exactly at the boundary,
 * which is where a toggle that reveals nothing is most obvious.
 */
/**
 * How tight the collapse is. Spelled as literal class names because Tailwind
 * cannot build one from a variable — the same reason the clamp was inlined here
 * before this prop existed.
 *
 * 🔴 DIAL-IN CLAMPS AT SIX, NOT FOUR. A description is prose and four lines is
 * a fair sample of it. A dial-in block is a phone number, a conference ID and a
 * PIN — every line of it is the thing the reader came for, and a clamp that
 * hides the PIN behind a "Show more" makes the card actively worse than not
 * rendering it. Six fits a phone number, a conference ID and a PIN with room to
 * spare, while still capping a pasted wall of text.
 *
 * 🔴 SIX IS ALSO THE CEILING TAILWIND GIVES US. The default `lineClamp` scale is
 * 1-6 and this config does not extend it, so `line-clamp-8` would compile, pass
 * typecheck, pass lint — and emit NO CSS. The block would render unclamped and
 * the toggle would never appear, because `overflowing` measures scrollHeight
 * against clientHeight and an unclamped element never overflows itself. Raising
 * this past 6 means extending the theme first.
 */
const CLAMP_CLASS = {
  4: "line-clamp-4",
  6: "line-clamp-6",
} as const;

function ExpandableDescription({
  text,
  lines = 4,
  testId = "appointment-description",
}: {
  text: string;
  lines?: keyof typeof CLAMP_CLASS;
  testId?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const bodyRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    // Only measurable while clamped: expanding removes the clamp, so
    // scrollHeight collapses to clientHeight and would read as "fits". Bail
    // early to keep the previous verdict — otherwise "Show less" would
    // disappear and strand the reader in the expanded state.
    if (expanded) return;
    const el = bodyRef.current;
    if (!el) return;
    // The +1 absorbs sub-pixel rounding: scrollHeight/clientHeight are
    // integers while line-height usually isn't, so an exact fit can measure a
    // pixel taller than its own box.
    const measure = () => setOverflowing(el.scrollHeight > el.clientHeight + 1);
    measure();
    // Re-measure while the drawer is resized — a description that fits at
    // desktop width can wrap past four lines on a narrow viewport. jsdom has
    // no real ResizeObserver, so the one-shot measure above is what tests see.
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, expanded]);

  return (
    <div className="mt-space-2">
      <p
        ref={bodyRef}
        data-testid={testId}
        className={cn(
          "m-0 text-small text-gray-mid whitespace-pre-line",
          // Tailwind can't build a class name from a variable (it would be
          // purged), so the four-line clamp is spelled out here.
          !expanded && CLAMP_CLASS[lines],
        )}
      >
        {text}
      </p>
      {overflowing && (
        <Button
          type="button"
          variant="text"
          size="sm"
          aria-expanded={expanded}
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded ? (
            <ChevronUp size={14} aria-hidden />
          ) : (
            <ChevronDown size={14} aria-hidden />
          )}
          {expanded ? "Show less" : "Show more"}
        </Button>
      )}
    </div>
  );
}

function AppointmentRow({
  appointment,
  tz,
  onEdit,
}: {
  appointment: AppointmentDto;
  tz: string;
  onEdit: (appointment: AppointmentDto) => void;
}) {
  const { toast } = useToast();
  const cancel = useCancelAppointment();
  const resend = useResendInvitations();
  const respond = useRespond(appointment.id);
  const cancelled = appointment.status === "Cancelled";
  const externalCount = appointment.externalAttendees?.length ?? 0;
  const internalCount = appointment.attendees?.length ?? 0;
  // Whether there is ANYBODY to re-send to. The button used to require an
  // external attendee, so an all-internal meeting — most of them — offered no
  // way to send the details again at all.
  //
  // This counts attendee ROWS, not reachable mailboxes: the server drops staff
  // who are deactivated or have no address, and asking the client to predict
  // that would mean shipping a second copy of a rule it cannot see. Offering the
  // action and having it reach nobody is the better failure — it is visible, and
  // the audit row says who was actually reached.
  const emailableCount = externalCount + internalCount;
  // null when the stored value is not an http(s) URL — see safeHref. The null is
  // an instruction to render the raw value as text, NOT a reason to fall back to
  // putting it in the href anyway.
  const joinUrl = safeHref(appointment.videoLink);

  // A repeating appointment asks which occurrences an action reaches, and warns
  // before a decline that leaves the whole series. A one-off does NEITHER — it
  // cancels and declines on the first click, exactly as it always has, and the
  // two dialogs below are not even mounted for it.
  const isSeries = Boolean(appointment.seriesId);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);

  // The series total, not a count of what is still to come — worded to match.
  const occurrenceCount = appointment.recurrence?.occurrenceCount ?? 0;
  // 🔴 Read off the occurrence, NOT hard-coded to 0. Every occurrence carries
  // the summary, and `detachedCount` exists precisely so the scope dialog can
  // say how many occurrences "All" will NOT reach — a warning the dialog's own
  // header calls out in red, because a reader told the number afterwards has
  // already made the choice. The `?? 0` covers only a shape the server never
  // sends (a seriesId with no summary), where no warning is the right answer.
  const detachedCount = appointment.recurrence?.detachedCount ?? 0;

  const timeLabel = appointment.isAllDay
    ? "All day"
    : `${formatTimeInTz(appointment.startAt, tz)} – ${formatTimeInTz(appointment.endAt, tz)}`;

  // `scope` is undefined on a one-off, and `appointmentsApi.cancel` appends the
  // query parameter only when it is set — so that request is byte-identical to
  // the one this surface has always sent.
  const doCancel = async (scope?: RecurrenceEditScope) => {
    try {
      await cancel.mutateAsync({ id: appointment.id, scope });
      toast({ message: "Appointment cancelled.", tone: "success" });
    } catch (err) {
      toast({ message: extractErrorMessage(err), tone: "error" });
    } finally {
      // Closed on failure too: the toast should not be read through a dialog,
      // and the row is still there to press Cancel on again.
      setScopeOpen(false);
    }
  };

  const doResend = async () => {
    try {
      await resend.mutateAsync({
        id: appointment.id,
        // Always true from this surface: the button says "all attendees", so it
        // has to mean it. The server still refuses to email internal attendees
        // when the kill switch is off, which is why this is a request rather
        // than a promise.
        includeInternalAttendees: true,
      });
      toast({
        message:
          emailableCount === 1
            ? "Invitation re-sent."
            : `Invitation re-sent to ${emailableCount} attendees.`,
        tone: "success",
      });
    } catch (err) {
      toast({ message: extractErrorMessage(err), tone: "error" });
    }
  };

  const doRespond = async (response: "Accepted" | "Declined") => {
    try {
      await respond.mutateAsync(response);
      toast({ message: `You ${response.toLowerCase()} the invite.`, tone: "success" });
    } catch (err) {
      toast({ message: extractErrorMessage(err), tone: "error" });
    }
  };

  // Declining a series occurrence is a decision about every future one, so it
  // is confirmed first. Accepting is not — it commits nobody to anything they
  // cannot undo by declining later.
  const onDeclineClick = () => {
    if (isSeries) {
      setDeclineOpen(true);
      return;
    }
    void doRespond("Declined");
  };

  return (
    <li className="rounded-lg border border-border-light bg-white-pure p-space-3">
      <div className="flex items-start justify-between gap-space-3">
        <div className="min-w-0">
          <div className="flex items-center gap-space-2 text-onyx">
            <span className="font-display font-semibold text-small truncate">
              {appointment.title}
            </span>
            {cancelled && <StatusBadge tone="danger">Cancelled</StatusBadge>}
          </div>
          <div className="mt-space-1 flex items-center gap-space-1 text-caption text-gray-mid">
            <Clock size={13} strokeWidth={1.5} aria-hidden />
            <span>{timeLabel}</span>
          </div>
        </div>
        {appointment.isOwner && !cancelled && (
          <div className="flex shrink-0 items-center gap-space-1">
            {/* Only offered when there is somebody to re-send TO. That used
                to mean external attendees alone, because they were the only
                recipients of an invitation email; staff attendees now count
                too. */}
            {emailableCount > 0 && (
              <Button
                variant="icon"
                size="icon"
                type="button"
                loading={resend.isPending}
                onClick={doResend}
                aria-label="Resend invitation to all attendees"
                title="Resend invitation to all attendees"
              >
                <Send size={15} strokeWidth={1.5} aria-hidden />
              </Button>
            )}
            <Button
              variant="icon"
              size="icon"
              type="button"
              onClick={() => onEdit(appointment)}
              aria-label="Edit appointment"
            >
              <Pencil size={15} strokeWidth={1.5} aria-hidden />
            </Button>
            <Button
              variant="icon"
              size="icon"
              type="button"
              loading={cancel.isPending}
              onClick={() => {
                if (isSeries) {
                  setScopeOpen(true);
                  return;
                }
                void doCancel();
              }}
              aria-label="Cancel appointment"
            >
              <X size={15} strokeWidth={1.5} aria-hidden />
            </Button>
          </div>
        )}
      </div>

      {/* The server's own sentence, rendered verbatim — it names the rule and
          the date the series stops, which is the half a single occurrence
          cannot tell you. Never recomposed client-side. */}
      {appointment.recurrence && (
        <div className="mt-space-2 flex items-start gap-space-1 text-caption text-gray-mid">
          <Repeat size={13} strokeWidth={1.5} className="shrink-0" aria-hidden />
          <span>{appointment.recurrence.description}</span>
        </div>
      )}

      {appointment.location && (
        <div className="mt-space-2 flex items-center gap-space-1 text-caption text-gray-mid">
          <MapPin size={13} strokeWidth={1.5} aria-hidden />
          <span className="truncate">{appointment.location}</span>
        </div>
      )}

      {/*
        Join details. Above the description on purpose: the description is
        context, this is how you actually get into the meeting, and on a video
        call the card otherwise showed a time and a street address with no way in.

        Each half renders independently — a meeting can have a link and no
        dial-in, or a dial-in and no link — so neither draws a heading the other
        would leave dangling, and a card with neither is byte-identical to today.

        Redaction needs no handling here: the server blanks both fields on a row
        the viewer neither owns nor attends, and this drawer's own read is
        own-scope anyway, so the values are simply absent rather than hidden.
      */}
      {joinUrl && (
        <a
          href={joinUrl}
          target="_blank"
          rel="noopener noreferrer"
          title="Join the meeting (opens in new tab)"
          className="mt-space-2 inline-flex items-center gap-space-1 text-caption
            text-gold-shadow hover:underline underline-offset-4"
        >
          <Video size={13} strokeWidth={1.5} aria-hidden />
          <span>Join the meeting</span>
        </a>
      )}

      {/*
        A video link that is not http(s) — a `javascript:` value, or a fragment
        the AI parser pulled out of pasted prose. Shown as TEXT, never as an
        anchor: a staff member has to be able to see what is stored so they can
        correct it, and `break-all` is what stops a 500-character URL pushing the
        card sideways.
      */}
      {!joinUrl && appointment.videoLink && (
        <div className="mt-space-2 flex items-start gap-space-1 text-caption text-gray-mid">
          <Video size={13} strokeWidth={1.5} aria-hidden className="mt-[2px] shrink-0" />
          <span className="min-w-0 break-all">{appointment.videoLink}</span>
        </div>
      )}

      {appointment.dialInInstructions && (
        <div className="mt-space-2 flex items-start gap-space-1 text-caption text-gray-mid">
          <Phone size={13} strokeWidth={1.5} aria-hidden className="mt-[2px] shrink-0" />
          <div className="min-w-0 flex-1">
            <ExpandableDescription
              text={appointment.dialInInstructions}
              lines={6}
              testId="appointment-dial-in"
            />
          </div>
        </div>
      )}

      {appointment.description && (
        <ExpandableDescription text={appointment.description} />
      )}

      {appointment.relatedEntity && (
        <div className="mt-space-2 text-caption">
          {appointment.relatedEntity.detailUrl ? (
            <Link
              to={appointment.relatedEntity.detailUrl}
              className="text-gold-shadow hover:text-onyx underline"
            >
              {appointment.relatedEntity.displayName}
            </Link>
          ) : (
            <span className="text-gray-mid">{appointment.relatedEntity.displayName}</span>
          )}
        </div>
      )}

      {appointment.attendees.length > 0 && (
        <div className="mt-space-3">
          <div className="mb-space-1 flex items-center gap-space-1 text-label uppercase tracking-label text-gray-mid">
            <Users size={12} strokeWidth={1.5} aria-hidden />
            Attendees
          </div>
          <div className="flex flex-wrap gap-space-1">
            {appointment.attendees.map((a) => (
              <StatusBadge key={a.userId} tone={RESPONSE_TONE[a.response]}>
                {a.displayName}
              </StatusBadge>
            ))}
          </div>
        </div>
      )}

      {appointment.myResponse && !appointment.isOwner && !cancelled && (
        <div className="mt-space-3 flex items-center gap-space-2">
          <Button
            variant={appointment.myResponse === "Accepted" ? "primary" : "secondary"}
            size="sm"
            type="button"
            loading={respond.isPending}
            onClick={() => doRespond("Accepted")}
          >
            Accept
          </Button>
          <Button
            variant={appointment.myResponse === "Declined" ? "destructive" : "secondary"}
            size="sm"
            type="button"
            loading={respond.isPending}
            onClick={onDeclineClick}
          >
            Decline
          </Button>
        </div>
      )}

      {/* Both dialogs are mounted ONLY for a series, so a one-off cannot grow a
          prompt by accident — the absence is structural, not a runtime branch
          inside the dialog. No extra fetch: this drawer is handed ONE
          occurrence, and everything the dialog needs rides its summary. */}
      {isSeries && (
        <RecurrenceScopeDialog
          open={scopeOpen}
          action="cancel"
          detachedCount={detachedCount}
          pending={cancel.isPending}
          onChoose={(scope) => void doCancel(scope)}
          onCancel={() => setScopeOpen(false)}
        />
      )}

      {isSeries && (
        <ConfirmModal
          open={declineOpen}
          destructive
          title="Decline the whole series?"
          message={
            <>
              <p className="m-0">
                This appointment repeats
                {occurrenceCount > 0
                  ? ` — ${occurrenceCount} occurrences in the series`
                  : ""}
                . Declining removes you from every future occurrence, not just
                this one.
              </p>
              <p className="m-0 mt-space-2">
                You would need to be invited again to return to it.
              </p>
            </>
          }
          confirmLabel="Decline every occurrence"
          cancelLabel="Keep my invite"
          onConfirm={() => {
            setDeclineOpen(false);
            void doRespond("Declined");
          }}
          onCancel={() => setDeclineOpen(false)}
        />
      )}
    </li>
  );
}
